import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../Button";
import Dropdown from "../Dropdown";
import SearchableDropdown from "../SearchableDropdown";
import Input from "../Input";
import DatePicker from "../DatePicker";
import PasswordField from "../PasswordField";
import TextArea from "../TextArea";
import apiClient from "../../../services/apiClient";
import useFormKeyboardNavigation from "../../../hooks/useFormKeyboardNavigation";

import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from "../../../utils/constants";

function normalizeFieldType(type = "text") {
  const normalized = String(type).trim().toLowerCase();
  const aliasMap = {
    upload: "file",
    "input multiline": "textarea",
    inputmultiline: "textarea",
    "cascading dropdown": "cascadingDropdown",
    datetime: "datetime",
    "date time": "datetime",
    "radio button": "radio",
    radiobutton: "radio",
  };
  return aliasMap[normalized] || normalized;
}

function getInitialValues(schema = [], initialValues = {}) {
  return schema.reduce((acc, field) => {
    const normalizedType = normalizeFieldType(field.type);
    const defaultValue = field.defaultValue ?? (normalizedType === "checkbox" ? false : "");
    acc[field.name] = initialValues[field.name] ?? defaultValue;
    return acc;
  }, {});
}

function isPdfFile(file) {
  if (!(file instanceof File)) return false;
  const fileName = String(file.name || "").toLowerCase();
  const mimeType = String(file.type || "").toLowerCase();
  return mimeType === "application/pdf" || fileName.endsWith(".pdf");
}

function validateFile(file, label, accept = ".pdf") {
  if (!file) return null;
  
  if (file.size > MAX_FILE_SIZE) {
    return `${label} must be less than ${MAX_FILE_SIZE_MB}MB`;
  }

  // If accept is strictly .pdf, enforce it
  if (accept === ".pdf" && !isPdfFile(file)) {
    return `${label} must be a PDF file`;
  }
  
  return null;
}

function buildInitialValuesKey(schema = [], initialValues = {}) {
  return JSON.stringify(
    schema.map((field) => {
      const value = initialValues[field.name];

      if (value instanceof File) {
        return [field.name, value.name, value.size, value.lastModified];
      }

      return [field.name, value ?? null];
    })
  );
}

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear());
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getCurrentDateTimeLocalString() {
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function validateForm(schema = [], values = {}) {
  const nextErrors = {};

  schema.forEach((field) => {
    const fieldType = normalizeFieldType(field.type);
    const value = values[field.name];
    const validation = field.validation || {};
    const rawStringValue = typeof value === "string" ? value.trim() : value;
    const stringValue = typeof rawStringValue === "string" ? rawStringValue : "";
    const isEmpty = value === "" || value === null || value === undefined;
    const hasValue = !isEmpty && !(typeof value === "string" && value.trim() === "");

    if (field.validation?.required) {
      if ((fieldType === "checkbox" && !value) || (fieldType !== "checkbox" && !hasValue)) {
        nextErrors[field.name] =
          field.validation?.message || `${field.label} is required`;
        return;
      }
    }

    if ((fieldType === "date" || fieldType === "datetime") && hasValue) {
      const year = String(value).split("-")[0];
      if (year && year.length > 4) {
        nextErrors[field.name] = `${field.label} must have a 4-digit year`;
        return;
      }

      if (Number.isNaN(Date.parse(value))) {
        nextErrors[field.name] = `${field.label} must be a valid date`;
        return;
      }

      if (validation.notFuture) {
        const currentDate = new Date(value);
        const today = new Date();

        if (fieldType === "date") {
          currentDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
        }

        if (currentDate > today) {
          nextErrors[field.name] =
            validation.notFutureMessage || `${field.label} cannot be in the future`;
          return;
        }
      }

      if (validation.notEarlierThan && values[validation.notEarlierThan]) {
        const earlierValue = values[validation.notEarlierThan];
        if (!Number.isNaN(Date.parse(earlierValue))) {
          const currentDate = new Date(value);
          const earlierDate = new Date(earlierValue);
          
          // Reset time part for date-only comparison if both are just dates
          if (fieldType === "date" && normalizeFieldType(schema.find(f => f.name === validation.notEarlierThan)?.type) === "date") {
            currentDate.setHours(0, 0, 0, 0);
            earlierDate.setHours(0, 0, 0, 0);
          }

          if (currentDate < earlierDate) {
            const earlierFieldLabel = schema.find(f => f.name === validation.notEarlierThan)?.label || validation.notEarlierThan;
            nextErrors[field.name] = validation.notEarlierThanMessage || `${field.label} cannot be earlier than ${earlierFieldLabel}`;
            return;
          }
        }
      }

      if (validation.minDate) {
        const currentDate = new Date(value);
        const minDateVal = new Date(validation.minDate);

        if (fieldType === "date") {
          currentDate.setHours(0, 0, 0, 0);
          minDateVal.setHours(0, 0, 0, 0);
        }

        if (currentDate < minDateVal) {
          nextErrors[field.name] =
            validation.minDateMessage || `${field.label} cannot be earlier than ${validation.minDate}`;
          return;
        }
      }
    }

    if (typeof stringValue === "string" && stringValue.length > 0) {
      if (typeof validation.equalLengthWith === "string") {
        const siblingRawValue = values[validation.equalLengthWith];
        const siblingValue =
          typeof siblingRawValue === "string" ? siblingRawValue.trim() : "";
        const siblingField = schema.find((f) => f.name === validation.equalLengthWith);
        const siblingLabel = siblingField?.label || validation.equalLengthWith;

        if (siblingValue.length > 0 && stringValue.length !== siblingValue.length) {
          nextErrors[field.name] =
            validation.equalLengthWithMessage ||
            `${field.label} length must be same as ${siblingLabel}`;
          return;
        }
      }

      if (
        typeof validation.minLength === "number" &&
        stringValue.length < validation.minLength
      ) {
        nextErrors[field.name] =
          validation.minLengthMessage ||
          `${field.label} must be at least ${validation.minLength} characters`;
        return;
      }

      if (
        typeof validation.maxLength === "number" &&
        stringValue.length > validation.maxLength
      ) {
        nextErrors[field.name] =
          validation.maxLengthMessage ||
          `${field.label} must be at most ${validation.maxLength} characters`;
        return;
      }

      if (validation.pattern instanceof RegExp && !validation.pattern.test(stringValue)) {
        nextErrors[field.name] =
          validation.patternMessage || `${field.label} format is invalid`;
        return;
      }
    }

    if ((fieldType === "number" || typeof validation.precision === "number" || validation.integer) && hasValue) {
      const normalizedValue = typeof value === "number" ? value : Number(String(value).trim());

      if (Number.isNaN(normalizedValue)) {
        nextErrors[field.name] = validation.numberMessage || `${field.label} must be a valid number`;
        return;
      }

      if (typeof validation.min === "number" && normalizedValue < validation.min) {
        nextErrors[field.name] = validation.minMessage || `${field.label} must be at least ${validation.min}`;
        return;
      }

      if (typeof validation.max === "number" && normalizedValue > validation.max) {
        nextErrors[field.name] = validation.maxMessage || `${field.label} must be at most ${validation.max}`;
        return;
      }

      if (typeof validation.min !== "number" && normalizedValue < 0) {
        nextErrors[field.name] = `${field.label} cannot be negative`;
        return;
      }

      if (validation.integer && !Number.isInteger(normalizedValue)) {
        nextErrors[field.name] = validation.integerMessage || `${field.label} must be an integer`;
        return;
      }

      if (typeof validation.precision === "number") {
        const decimals = String(value).trim().split(".")[1]?.length || 0;
        if (decimals > validation.precision) {
          nextErrors[field.name] =
            validation.precisionMessage ||
            `${field.label} can have at most ${validation.precision} decimal places`;
          return;
        }
      }
    }

    if (fieldType === "file" && value instanceof File) {
      const fileError = validateFile(value, field.label, field.accept);
      if (fileError) {
        nextErrors[field.name] = fileError;
        return;
      }

      if (typeof validation.maxLength === "number" && value.name.length > validation.maxLength) {
        nextErrors[field.name] =
          validation.maxLengthMessage ||
          `${field.label} file name must be at most ${validation.maxLength} characters`;
        return;
      }

      if (validation.accept?.length) {
        const allowedTypes = validation.accept.map((type) => String(type).toLowerCase());
        const fileName = String(value.name || "").toLowerCase();
        const mimeType = String(value.type || "").toLowerCase();
        const isAccepted = allowedTypes.some((type) => {
          if (type.startsWith(".")) return fileName.endsWith(type);
          return mimeType === type;
        });

        if (!isAccepted) {
          nextErrors[field.name] =
            validation.acceptMessage || `${field.label} has an unsupported file type`;
          return;
        }
      }

      if (typeof validation.maxFileNameLength === "number" && value.name.length > validation.maxFileNameLength) {
        nextErrors[field.name] =
          validation.maxFileNameLengthMessage ||
          `${field.label} file name must be at most ${validation.maxFileNameLength} characters`;
      }
    }
  });

  return nextErrors;
}

function CrudForm({
  mode = "create",
  schema = [],
  initialValues = {},
  isSubmitting = false,
  onSubmit,
  onCancel,
  onValuesChange,
  topAction = null,
}) {
  const formRef = useRef(null);
  const [values, setValues] = useState(() =>
    getInitialValues(schema, initialValues)
  );
  const [errors, setErrors] = useState({});
  const [filePreviewMap, setFilePreviewMap] = useState({});
  const [pdfRenderUrlMap, setPdfRenderUrlMap] = useState({});
  const [pdfLoadingMap, setPdfLoadingMap] = useState({});
  const lastInitialValuesKeyRef = useRef(null);
  const { focusNextField } = useFormKeyboardNavigation(
    formRef,
    `${mode}:${isSubmitting}:${buildInitialValuesKey(schema, initialValues)}`
  );

  useEffect(() => {
    const nextInitialValuesKey = `${mode}:${buildInitialValuesKey(schema, initialValues)}`;

    if (lastInitialValuesKeyRef.current === nextInitialValuesKey) {
      return;
    }

    lastInitialValuesKeyRef.current = nextInitialValuesKey;
    const nextValues = getInitialValues(schema, initialValues);
    setValues(nextValues);
    setErrors({});
    if (onValuesChange) {
      onValuesChange(nextValues);
    }

    // Reset previews only when the form is genuinely loading a different record/initial state.
    const initialPreviews = {};
    schema.forEach((field) => {
      const val = initialValues[field.name];
      if (normalizeFieldType(field.type) === "file" && typeof val === "string" && val) {
        initialPreviews[field.name] = val;
      }
    });
    setFilePreviewMap(initialPreviews);
    setPdfRenderUrlMap({});
    setPdfLoadingMap({});
  }, [schema, initialValues, mode]);

  // Resolve PDF URLs to blob URLs when needed so browsers can render them inline.
  useEffect(() => {
    const pdfEntries = Object.entries(filePreviewMap).filter(([fieldName, preview]) => {
      const selectedFile = values[fieldName];
      return (
        String(preview || "").toLowerCase().includes(".pdf") ||
        (selectedFile instanceof File && selectedFile.type === "application/pdf")
      );
    });

    if (!pdfEntries.length) {
      setPdfRenderUrlMap({});
      setPdfLoadingMap({});
      return;
    }

    let isMounted = true;
    const createdUrls = [];

    const syncPdfRenderUrls = async () => {
      const nextRenderUrls = {};
      const nextLoadingState = {};

      await Promise.all(
        pdfEntries.map(async ([fieldName, preview]) => {
          const previewUrl = String(preview);

          if (previewUrl.startsWith("blob:")) {
            nextRenderUrls[fieldName] = previewUrl;
            nextLoadingState[fieldName] = false;
            return;
          }

          nextLoadingState[fieldName] = true;

          try {
            const response = await apiClient.get(previewUrl, {
              responseType: "blob",
              baseURL: previewUrl.startsWith("http") ? "" : undefined,
            });
            const blob = new Blob([response.data], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(blob);
            createdUrls.push(blobUrl);
            nextRenderUrls[fieldName] = blobUrl;
          } catch (err) {
            console.error("Failed to fetch PDF for inline preview:", err);
            nextRenderUrls[fieldName] = previewUrl;
          } finally {
            nextLoadingState[fieldName] = false;
          }
        })
      );

      if (isMounted) {
        setPdfRenderUrlMap(nextRenderUrls);
        setPdfLoadingMap(nextLoadingState);
      }
    };

    syncPdfRenderUrls();

    return () => {
      isMounted = false;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [filePreviewMap, values]);

  const cascadingOptionsMap = useMemo(() => {
    return schema.reduce((acc, field) => {
      if (normalizeFieldType(field.type) === "cascadingDropdown") {
        const parentValue = field.parentField ? values[field.parentField] : "";
        const optionMap = field.optionMap || {};
        acc[field.name] = optionMap[parentValue] || [];
      }
      return acc;
    }, {});
  }, [schema, values]);

  const updateValue = (name, value) => {
    const nextValues = { ...values, [name]: value };
    setValues(nextValues);
    setErrors((current) => ({ ...current, [name]: undefined }));
    if (onValuesChange) {
      onValuesChange(nextValues);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const submitIntent = event?.nativeEvent?.submitter?.value || "default";
    const validationErrors = validateForm(schema, values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    await onSubmit(values, submitIntent);
  };

  return (
    <form
      ref={formRef}
      className="animate-in fade-in duration-300"
      onSubmit={submit}
    >
      {topAction ? <div className="mb-3 flex justify-start">{topAction}</div> : null}
      {/* GRID FOR MAIN FIELDS */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {schema
          .filter((f) => f.name !== "isActive")
          .map((field) => {
            const error = errors[field.name];
            const fieldType = normalizeFieldType(field.type);

            const commonProps = {
              key: field.name,
              label: field.label,
              name: field.name,
              value: values[field.name] ?? "",
              error,
              hint: field.hint,
            };

            const labelClass =
              "text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400";

            // DATE
            if (fieldType === "date") {
              const maxDateValue = field.validation?.notFuture ? getTodayDateString() : undefined;
              return (
                <DatePicker
                  {...commonProps}
                  placeholder={field.placeholder}
                  labelClassName={labelClass}
                  onChange={(val) => updateValue(field.name, val)}
                  maxDate={maxDateValue}
                  minDate={field.validation?.minDate}
                />
              );
            }

            // TEXT / DATETIME
            if (
              fieldType === "text" ||
              fieldType === "datetime"
            ) {
              const isNumericField = field.validation?.integer || typeof field.validation?.precision === "number";
              const maxDateValue = field.validation?.notFuture
                ? getCurrentDateTimeLocalString()
                : undefined;
              return (
                <Input
                  {...commonProps}
                  type={
                    fieldType === "datetime"
                      ? "datetime-local"
                      : fieldType
                  }
                  placeholder={field.placeholder}
                  labelClassName={labelClass}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (field.validation?.restrictTo === "digits") {
                      val = val.replace(/\D/g, "");
                    }
                    if (isNumericField && val.includes("-")) {
                      val = val.replace(/-/g, "");
                    }
                    updateValue(field.name, val);
                  }}
                  onKeyDown={(e) => {
                    if (isNumericField && (e.key === "-" || e.key === "e" || e.key === "E")) {
                      e.preventDefault();
                    }
                  }}
                  autoComplete={field.autoComplete}
                  maxLength={field.validation?.maxLength}
                  minLength={field.validation?.minLength}
                  max={maxDateValue}
                />
              );
            }

            // NUMBER
            if (fieldType === "number") {
              return (
                <Input
                  {...commonProps}
                  type="number"
                  min={field.validation?.min ?? 0}
                  max={field.validation?.max}
                  step={field.validation?.precision ? 1 / Math.pow(10, field.validation.precision) : "any"}
                  placeholder={field.placeholder}
                  labelClassName={labelClass}
                  onChange={(e) => {
                    const val = e.target.value;
                    const maxLength = field.validation?.maxLength;
                    if (typeof maxLength === "number" && val.length > maxLength) {
                      return;
                    }
                    if (val === "" || Number(val) >= 0) {
                      updateValue(field.name, val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "E") {
                      e.preventDefault();
                    }
                  }}
                />
              );
            }

            // PASSWORD
            if (fieldType === "password") {
              return (
                <PasswordField
                  {...commonProps}
                  placeholder={field.placeholder}
                  labelClassName={labelClass}
                  onChange={(e) => updateValue(field.name, e.target.value)}
                  autoComplete={field.autoComplete}
                />
              );
            }

            // TEXTAREA
            if (fieldType === "textarea") {
              return (
                <div key={field.name} className="md:col-span-2 lg:col-span-3">
                  <TextArea
                    {...commonProps}
                    rows={field.rows || 3}
                    placeholder={field.placeholder}
                    labelClassName={labelClass}
                    onChange={(e) => updateValue(field.name, e.target.value)}
                    maxLength={field.validation?.maxLength}
                  />
                </div>
              );
            }

            // DROPDOWN
            if (fieldType === "dropdown") {
              return (
                <Dropdown
                  {...commonProps}
                  options={field.options || []}
                  placeholder={field.placeholder || "Select Option"}
                  labelClassName={labelClass}
                  onChange={(e) => updateValue(field.name, e.target.value)}
                  onAdd={field.onAdd}
                />
              );
            }

            // SEARCHABLE DROPDOWN
            if (fieldType === "searchabledropdown") {
              return (
                <SearchableDropdown
                  {...commonProps}
                  options={field.options || []}
                  placeholder={field.placeholder || "Search..."}
                  labelClassName={labelClass}
                  onChange={(e) => updateValue(field.name, e.target.value)}
                  onAdd={field.onAdd}
                  onSelectComplete={() => {
                    const toggle = formRef.current?.querySelector(
                      `#${field.name}-searchable-dropdown-toggle`
                    );
                    if (toggle) {
                      focusNextField(toggle);
                    }
                  }}
                />
              );
            }

            // CASCADING DROPDOWN
            if (fieldType === "cascadingDropdown") {
              const options = cascadingOptionsMap[field.name] || [];
              const disabled = field.parentField && !values[field.parentField];

              return (
                <SearchableDropdown
                  {...commonProps}
                  options={options}
                  disabled={disabled}
                  placeholder={field.placeholder || "Select..."}
                  labelClassName={labelClass}
                  onChange={(e) => updateValue(field.name, e.target.value)}
                  onAdd={field.onAdd}
                  onSelectComplete={() => {
                    const toggle = formRef.current?.querySelector(
                      `#${field.name}-searchable-dropdown-toggle`
                    );
                    if (toggle) {
                      focusNextField(toggle);
                    }
                  }}
                />
              );
            }

            // FILE
            if (fieldType === "file") {
              const preview = filePreviewMap[field.name];
              const isPdf =
                preview &&
                (String(preview).toLowerCase().includes(".pdf") ||
                  (values[field.name] instanceof File &&
                    values[field.name].type === "application/pdf"));
              const pdfRenderUrl = pdfRenderUrlMap[field.name];
              const isPdfLoading = Boolean(pdfLoadingMap[field.name]);

              return (
                <div key={field.name} className="space-y-1">
                  <label className={labelClass}>{field.label}</label>

                  <div className="flex items-center gap-3">
                    <label className="group flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-white px-3 py-1.5 transition-all hover:border-primary-500 hover:bg-primary-50 dark:border-slate-800 dark:bg-slate-900/50">
                      <svg
                        className="h-4 w-4 text-slate-400 group-hover:text-primary-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <span className="text-xs font-bold text-slate-600 group-hover:text-primary-600">
                        Choose File
                      </span>
                      <input
                        type="file"
                        accept={field.accept || "application/pdf,.pdf"}
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (!file) {
                            updateValue(field.name, null);
                            setFilePreviewMap((c) => ({
                              ...c,
                              [field.name]: null,
                            }));
                            return;
                          }

                          const fileError = validateFile(file, field.label, field.accept);
                          if (fileError) {
                            setErrors((current) => ({
                              ...current,
                              [field.name]: fileError,
                            }));
                            event.target.value = "";
                            return;
                          }

                          updateValue(field.name, file);
                          const previewUrl = URL.createObjectURL(file);
                          setFilePreviewMap((c) => ({
                            ...c,
                            [field.name]: previewUrl,
                          }));
                        }}
                      />
                    </label>

                    {preview && (
                      <div className="flex items-center gap-3">
                        {isPdf ? (
                          <div className="relative h-16 w-16  rounded-lg border border-slate-200 bg-white shadow-sm">
                            {isPdfLoading ? (
                              <div className="flex h-full w-full items-center justify-center text-xs font-bold text-slate-400">
                                ...
                              </div>
                            ) : pdfRenderUrl ? (
                              <div className="absolute inset-0">
                                <iframe
                                  src={`${pdfRenderUrl}#toolbar=0&navpanes=0&scrollbar=0&page=1&view=FitH`}
                                  title={`${field.label} preview`}
                                  className="pointer-events-none absolute left-1/2 top-0 h-[220%] w-[220%] max-w-none -translate-x-1/2 border-0"
                                />
                              </div>
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-xs font-black text-danger">
                                PDF
                              </div>
                            )}
                          </div>
                        ) : (
                          <img
                            src={preview}
                            alt="preview"
                            className="h-16 w-16 rounded-lg border border-slate-200 object-cover shadow-sm"
                          />
                        )}
                        <a
                          href={isPdf ? pdfRenderUrl || preview : preview}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 hover:text-primary-800"
                        >
                          View
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            updateValue(field.name, null);
                            setFilePreviewMap((c) => ({
                              ...c,
                              [field.name]: null,
                            }));
                          }}
                          className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 hover:text-rose-800"
                        >
                          {values[field.name] instanceof File ? "Cancel" : "Delete"}
                        </button>
                      </div>
                    )}
                  </div>

                  {error && (
                    <p className="text-[10px] font-medium text-danger">{error}</p>
                  )}
                </div>
              );
            }

            // CHECKBOX (General)
            if (fieldType === "checkbox") {
              return (
                <div key={field.name} className="flex h-full items-center">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                    <input
                      type="checkbox"
                      checked={Boolean(values[field.name])}
                      onChange={(e) =>
                        updateValue(field.name, e.target.checked)
                      }
                      className="h-3 w-3 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
                    />
                    <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                      {field.label}
                    </span>
                  </label>
                </div>
              );
            }

            // RADIO
            if (fieldType === "radio") {
              return (
                <div key={field.name} className="space-y-1">
                  <p className={labelClass}>{field.label}</p>

                  <div className="flex flex-wrap gap-3">
                    {(field.options || []).map((option) => (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-2"
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value={option.value}
                          checked={values[field.name] === option.value}
                          onChange={(e) =>
                            updateValue(field.name, e.target.value)
                          }
                          className="h-3 w-3 border-slate-300 text-primary-600 focus:ring-primary-600/20"
                        />
                        <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {error && (
                    <p className="text-[10px] font-medium text-danger">{error}</p>
                  )}
                </div>
              );
            }

            // DEFAULT INPUT
            return (
              <Input
                {...commonProps}
                placeholder={field.placeholder}
                labelClassName={labelClass}
                onChange={(e) => updateValue(field.name, e.target.value)}
                autoComplete={field.autoComplete}
              />
            );
          })}
      </div>

      {/* IS ACTIVE CHECKBOX (Always Left) */}
      {schema.find((f) => f.name === "isActive") && (
        <div className="mt-2 flex justify-start">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
            <input
              type="checkbox"
              checked={Boolean(values["isActive"])}
              onChange={(e) => updateValue("isActive", e.target.checked)}
              className="h-3 w-3 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
            />
            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
              {schema.find((f) => f.name === "isActive").label}
            </span>
          </label>
        </div>
      )}

      {/* ACTION BUTTONS (Left Aligned) */}
      <div className="mt-3 flex justify-start gap-2 border-t border-slate-100 pt-2 dark:border-slate-800">
        <Button
          type="submit"
          name="submitIntent"
          value="default"
          variant="primary"
          size="sm"
          isLoading={isSubmitting}
          className="px-4 shadow-lg shadow-primary-500/10"
        >
          {mode === "edit" ? "Update Record" : "Create Record"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default CrudForm;

